/**
 * Authentication store using Zustand
 * Manages auth state, token refresh, and permissions
 */

import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import {
  AuthState,
  AuthUser,
  Permission,
  PermissionType,
} from '@/types/auth'
import { authApi, tokenManager } from '@/services/authApi'

const DEV_MODE_AUTH_KEY = 'dev_mode_auth'

const isDevMode = (): boolean =>
  import.meta.env?.MODE === 'development' || process.env.NODE_ENV === 'development'

const isAuthDisabled = (): boolean =>
  import.meta.env?.VITE_DISABLE_AUTH === 'true'

function createDevUser(): AuthUser {
  return {
    id: 'dev-user-id',
    email: 'dev@localhost',
    first_name: 'Dev',
    last_name: 'User',
    is_active: true,
    is_verified: true,
    created_at: new Date().toISOString(),
    role: 'super_admin',
    permissions: Object.values(Permission),
    is_super_admin: true,
    subscription_active: true,
    subscription_tier: 'enterprise',
    has_app_access: true,
    is_client_admin: true,
    is_superuser_for_app: true,
    // OR-312: dev user is tenant-wide so local dev sees every IBS.
    data_scopes: { IBS: ['*'] },
  }
}

// Legacy wildcard sentinel that still resolves to full access (rows migrated
// away). Under the CD-aligned model, "no row on an axis" already means full.
const TENANT_WIDE_SENTINEL = '*'
// Reserved id marking an explicit "no access" on an axis (matches the backend).
const NO_ACCESS_SENTINEL = '__NO_ACCESS__'

// Maps a UI resource type to its data-scope axis key.
const RESOURCE_SCOPE_KEY: Record<string, string> = {
  ibs: 'IBS',
  microservice: 'Microservices',
  system: 'Systems',
  infrastructure: 'Infrastructure',
  location: 'Locations',
  team: 'Teams',
  supplier: 'Suppliers',
}

interface AuthActions {
  // Auth actions
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => Promise<void>
  logout: () => Promise<void>
  refreshTokens: () => Promise<boolean>
  devSignIn: () => void

  // User actions
  fetchUser: () => Promise<void>

  // Permission helpers
  hasPermission: (permission: PermissionType) => boolean
  hasAnyPermission: (permissions: PermissionType[]) => boolean
  hasAllPermissions: (permissions: PermissionType[]) => boolean

  // RBAC v1.2 helpers
  isClientAdmin: () => boolean
  hasAppAccess: () => boolean
  isSubscriptionActive: () => boolean
  isSuperuser: () => boolean

  // OR-312 Group E: data scope helpers
  getDataScopes: () => Record<string, string[]>
  isTenantWideIbs: () => boolean
  canAccessResource: (resourceType: string, resourceId: string) => boolean

  // State actions
  setLoading: (isLoading: boolean) => void
  reset: () => void
  initFromStorage: () => Promise<void>

  // Internal: token refresh scheduler
  scheduleTokenRefresh: (expiresIn: number) => void
}

type AuthStore = AuthState & AuthActions

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoading: true,
  isAuthenticated: false,
  tokenExpiresAt: null,
}

// Token refresh interval (refresh 1 minute before expiry)
const TOKEN_REFRESH_BUFFER = 60 * 1000 // 1 minute in ms
let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        login: async (email: string, password: string) => {
          set({ isLoading: true })
          try {
            const tokens = await authApi.login({ email, password })
            tokenManager.setTokens(tokens)

            set({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              tokenExpiresAt: Date.now() + tokens.expires_in * 1000,
              isAuthenticated: true,
            })

            // Fetch user
            await get().fetchUser()

            // Schedule token refresh
            get().scheduleTokenRefresh(tokens.expires_in * 1000)

            set({ isLoading: false })
          } catch (error) {
            set({ isLoading: false })
            throw error
          }
        },

        register: async (
          email: string,
          password: string,
          firstName?: string,
          lastName?: string
        ) => {
          set({ isLoading: true })
          try {
            const tokens = await authApi.register({
              email,
              password,
              first_name: firstName,
              last_name: lastName,
            })
            tokenManager.setTokens(tokens)

            set({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              tokenExpiresAt: Date.now() + tokens.expires_in * 1000,
              isAuthenticated: true,
            })

            // Fetch user
            await get().fetchUser()

            // Schedule token refresh
            get().scheduleTokenRefresh(tokens.expires_in * 1000)

            set({ isLoading: false })
          } catch (error) {
            set({ isLoading: false })
            throw error
          }
        },

        devSignIn: () => {
          if (!isDevMode()) return

          const devUser = createDevUser()
          const mockToken = 'dev-token-' + Date.now()
          localStorage.setItem(DEV_MODE_AUTH_KEY, 'true')
          localStorage.setItem('access_token', mockToken)

          set({
            user: devUser,
            accessToken: mockToken,
            isAuthenticated: true,
            isLoading: false,
          })
        },

        logout: async () => {
          try {
            // Only call backend logout if not a dev mode session
            if (!localStorage.getItem(DEV_MODE_AUTH_KEY)) {
              await authApi.logout()
            }
          } finally {
            localStorage.removeItem(DEV_MODE_AUTH_KEY)
            tokenManager.clearTokens()
            if (refreshTimeoutId) {
              clearTimeout(refreshTimeoutId)
              refreshTimeoutId = null
            }
            set(initialState)
            set({ isLoading: false })
          }
        },

        refreshTokens: async () => {
          const { refreshToken } = get()
          if (!refreshToken) return false

          try {
            const tokens = await authApi.refreshToken(refreshToken)
            tokenManager.setTokens(tokens)

            set({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              tokenExpiresAt: Date.now() + tokens.expires_in * 1000,
            })

            // Schedule next refresh
            get().scheduleTokenRefresh(tokens.expires_in * 1000)

            return true
          } catch (error) {
            // Refresh failed - logout
            await get().logout()
            return false
          }
        },

        fetchUser: async () => {
          // Step 1: Get user info from /auth/me (access flags, roles, no permissions)
          const user = await authApi.getCurrentUser()

          // Step 2: Fetch permissions from /auth/permissions (Consumer Duty pattern)
          // Permissions are always resolved from the database, not the Auth0 token.
          try {
            const permData = await authApi.getPermissions()
            user.permissions = permData.permissions || []
            // OR-312 Group E: capture the user's data scope for client-side gating.
            user.data_scopes = permData.data_scopes || {}
            // Update role from DB roles if available
            if (permData.roles && permData.roles.length > 0 && !user.role) {
              user.role = permData.roles[0]
            }
          } catch (permError) {
            // Continue with empty permissions rather than blocking auth
            user.permissions = user.permissions || []
            user.data_scopes = user.data_scopes || {}
          }

          set({ user })
        },

        hasPermission: (permission: PermissionType) => {
          const { user } = get()
          if (!user) return false
          // Client admins have access to everything
          if (user.is_client_admin) return true
          return user.permissions.includes(permission)
        },

        hasAnyPermission: (permissions: PermissionType[]) => {
          const { user } = get()
          if (!user) return false
          // Client admins have access to everything
          if (user.is_client_admin) return true
          return permissions.some(p => user.permissions.includes(p))
        },

        hasAllPermissions: (permissions: PermissionType[]) => {
          const { user } = get()
          if (!user) return false
          // Client admins have access to everything
          if (user.is_client_admin) return true
          return permissions.every(p => user.permissions.includes(p))
        },

        isClientAdmin: () => {
          const { user } = get()
          if (!user) return false
          return user.is_client_admin === true
        },

        hasAppAccess: () => {
          const { user } = get()
          if (!user) return false
          // Client Admins always have app access
          if (user.is_client_admin) return true
          return user.has_app_access === true
        },

        isSubscriptionActive: () => {
          const { user } = get()
          if (!user) return false
          return user.subscription_active === true
        },

        isSuperuser: () => {
          const { user } = get()
          if (!user) return false
          return user.is_superuser_for_app === true
        },

        // OR-312 Group E: the raw per-axis data scope map for the current user.
        getDataScopes: () => {
          const { user } = get()
          return user?.data_scopes ?? {}
        },

        // True when the user has full IBS visibility. Allow-by-default: no IBS
        // row (or the legacy ["*"]) means full access; ["__NO_ACCESS__"] or a
        // concrete list does not.
        isTenantWideIbs: () => {
          const { user } = get()
          if (!user) return false
          if (user.is_client_admin) return true
          const ibs = user.data_scopes?.IBS
          if (!ibs || ibs.length === 0) return true
          if (ibs.includes(NO_ACCESS_SENTINEL)) return false
          return ibs.includes(TENANT_WIDE_SENTINEL)
        },

        // Whether the user may access a single resource, mirroring the backend
        // resolver's allow-by-default semantics for client-side gating.
        //  - Client admins: full access.
        //  - IBS: full unless restricted to a concrete list or denied via
        //    ["__NO_ACCESS__"].
        //  - Components: an IBS deny cascades; otherwise an explicit component
        //    override is honoured, else we defer to the server (which only ever
        //    returns in-scope rows), so a rendered component is accessible.
        canAccessResource: (resourceType: string, resourceId: string) => {
          const { user } = get()
          if (!user) return false
          if (user.is_client_admin) return true

          const scopes = user.data_scopes ?? {}
          const ibs = scopes.IBS
          const ibsDeny = Array.isArray(ibs) && ibs.includes(NO_ACCESS_SENTINEL)
          const ibsFull = !ibs || ibs.length === 0 || ibs.includes(TENANT_WIDE_SENTINEL)

          if (resourceType === 'ibs') {
            if (ibsDeny) return false
            if (ibsFull) return true
            return ibs.includes(resourceId)
          }

          // Components inherit an IBS deny.
          if (ibsDeny) return false
          const key = RESOURCE_SCOPE_KEY[resourceType]
          if (!key) return ibsFull

          const ids = scopes[key]
          if (ids && ids.includes(NO_ACCESS_SENTINEL)) return false
          if (ids && ids.includes(TENANT_WIDE_SENTINEL)) return true
          if (ids && ids.length > 0) return ids.includes(resourceId)

          // No explicit component override: defer to server-side filtering.
          return true
        },

        setLoading: (isLoading: boolean) => set({ isLoading }),

        reset: () => {
          tokenManager.clearTokens()
          if (refreshTimeoutId) {
            clearTimeout(refreshTimeoutId)
            refreshTimeoutId = null
          }
          set(initialState)
          set({ isLoading: false })
        },

        initFromStorage: async () => {
          // Auth bypass for E2E tests — auto-sign-in with dev user
          if (isAuthDisabled()) {
            const devUser = createDevUser()
            const mockToken = 'e2e-bypass-token-' + Date.now()
            localStorage.setItem(DEV_MODE_AUTH_KEY, 'true')
            localStorage.setItem('access_token', mockToken)
            set({
              user: devUser,
              accessToken: mockToken,
              isAuthenticated: true,
              isLoading: false,
            })
            return
          }

          // Restore dev mode session without hitting the backend
          if (isDevMode() && localStorage.getItem(DEV_MODE_AUTH_KEY)) {
            const devUser = createDevUser()
            const mockToken = localStorage.getItem('access_token') || 'dev-token-restored'
            // Ensure token is in localStorage so the request interceptor can
            // attach it to outgoing requests (it may have been removed by a
            // prior clearAuthAndRedirect call from a 401 response).
            localStorage.setItem('access_token', mockToken)
            set({
              user: devUser,
              accessToken: mockToken,
              isAuthenticated: true,
              isLoading: false,
            })
            return
          }

          const { accessToken, refreshToken, expiresAt } = tokenManager.initFromStorage()

          // Auth0 token check: if no legacy tokens but an Auth0 token exists, fetch user from /me
          if (!accessToken || !refreshToken) {
            const auth0Token = localStorage.getItem('auth0_access_token')
            if (auth0Token) {
              try {
                // Set the Auth0 token as Authorization header before fetching
                const { default: api } = await import('@/services/api')
                api.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
                await get().fetchUser()
                set({ isAuthenticated: true, isLoading: false })
              } catch {
                // Token is invalid/expired — clear it so ProtectedRoute
                // redirects to login instead of showing a blank page.
                localStorage.removeItem('auth0_access_token')
                set({ isAuthenticated: false, isLoading: false, user: null })
              }
              return
            }
            set({ isLoading: false })
            return
          }

          // Check if token is expired
          if (tokenManager.isTokenExpired(TOKEN_REFRESH_BUFFER / 1000)) {
            // Try to refresh
            set({
              refreshToken,
              isLoading: true,
            })
            const success = await get().refreshTokens()
            if (!success) {
              set({ isLoading: false })
              return
            }
          } else {
            set({
              accessToken,
              refreshToken,
              tokenExpiresAt: expiresAt,
              isAuthenticated: true,
            })
          }

          // Fetch user info
          try {
            await get().fetchUser()

            // Schedule token refresh
            const timeUntilExpiry = (expiresAt || 0) - Date.now()
            if (timeUntilExpiry > 0) {
              get().scheduleTokenRefresh(timeUntilExpiry)
            }
          } catch (error) {
            // If fetch fails, clear auth
            get().reset()
          }

          set({ isLoading: false })
        },

        // Internal: schedule automatic token refresh
        scheduleTokenRefresh: (expiresIn: number) => {
          if (refreshTimeoutId) {
            clearTimeout(refreshTimeoutId)
          }

          // Refresh 1 minute before expiry
          const refreshIn = expiresIn - TOKEN_REFRESH_BUFFER
          if (refreshIn <= 0) {
            get().refreshTokens()
            return
          }

          refreshTimeoutId = setTimeout(() => {
            get().refreshTokens()
          }, refreshIn)
        },
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: () => ({}), // Don't persist any auth state to storage
      }
    ),
    { name: 'AuthStore' }
  )
)

// Extend store with scheduleTokenRefresh (not exposed in interface)
declare module './authStore' {
  interface AuthActions {
    scheduleTokenRefresh: (expiresIn: number) => void
  }
}

export default useAuthStore
